import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '@/app/hooks';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { EmptyAnalyticsState } from './EmptyAnalyticsState';

/**
 * Monthly charts for registrations and offer clicks.
 * Displays data based on the filtered results from the store.
 */
export const OverviewCharts: React.FC = () => {
  const { t } = useTranslation('analytics');
  const { registrationsGraph, offerClicksGraph } = useAppSelector(
    (state) => state.analytics.overview,
  );

  const monthLabelsList = [
    t('months.jan'),
    t('months.feb'),
    t('months.mar'),
    t('months.apr'),
    t('months.may'),
    t('months.jun'),
    t('months.jul'),
    t('months.aug'),
    t('months.sep'),
    t('months.oct'),
    t('months.nov'),
    t('months.dec'),
  ];

  const hasRegistrationsData =
    registrationsGraph?.currentYear.some((point) => point.value > 0) ||
    registrationsGraph?.previousYear.some((point) => point.value > 0);

  const hasOfferClicksData = offerClicksGraph?.currentYear.some((point) => point.value > 0);

  const registrationSeriesDataList =
    registrationsGraph?.currentYear.map((point, pointIndex) => ({
      name: monthLabelsList[pointIndex],
      current: point.value,
      previous: registrationsGraph.previousYear[pointIndex]?.value || 0,
    })) || [];

  const clickSeriesDataList =
    offerClicksGraph?.currentYear.map((point, pointIndex) => ({
      name: monthLabelsList[pointIndex],
      current: point.value,
    })) || [];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 min-h-[400px]">
      <div className="bg-white border border-neutral-100 rounded-[20px] p-6 flex flex-col gap-4 h-full">
        <h3 className="text-xl font-medium text-dark">{t('overview.charts.registrationsTitle')}</h3>
        {hasRegistrationsData ? (
          <ResponsiveContainer width="100%" height={300}>
            <LineChart data={registrationSeriesDataList}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F3F3F4" />
              <XAxis
                dataKey="name"
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#6D6E7A', fontSize: 12 }}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#6D6E7A', fontSize: 12 }}
                allowDecimals={false}
              />
              <Tooltip />
              <Legend verticalAlign="top" align="left" iconType="circle" />
              <Line
                type="monotone"
                dataKey="current"
                name={t('overview.charts.currentYear')}
                stroke="#2C6CFF"
                strokeWidth={3}
                dot={false}
                activeDot={{ r: 6 }}
              />
              <Line
                type="monotone"
                dataKey="previous"
                name={t('overview.charts.previousYear')}
                stroke="#95B6FF"
                strokeWidth={3}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        ) : (
          <EmptyAnalyticsState />
        )}
      </div>

      <div className="bg-white border border-neutral-100 rounded-[20px] p-6 flex flex-col gap-4 h-full">
        <h3 className="text-xl font-medium text-dark">{t('overview.charts.offerClicksTitle')}</h3>
        {hasOfferClicksData ? (
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={clickSeriesDataList}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#F3F3F4" />
              <XAxis
                dataKey="name"
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#6D6E7A', fontSize: 12 }}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
                tick={{ fill: '#6D6E7A', fontSize: 12 }}
                allowDecimals={false}
              />
              <Tooltip />
              <Bar
                dataKey="current"
                name={t('overview.charts.clicks')}
                fill="#2C6CFF"
                radius={[4, 4, 0, 0]}
                barSize={20}
              />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <EmptyAnalyticsState />
        )}
      </div>
    </div>
  );
};
